/**
 * Features that can be fully unavailable on lower tariffs.
 * Add a key here when a new feature becomes tariff-gated.
 */
export const TARIFF_FEATURES = {
  /** Creating, renaming and deleting roles of your own. */
  CUSTOM_ROLES: 'custom_roles',
  /** Changing the permission matrix of the built-in (system) roles. */
  EDIT_SYSTEM_ROLES: 'edit_system_roles',
  /** The in-app AI assistant (orchestrator): launcher, drawer and its tools. */
  AI_ASSISTANT: 'ai_assistant',
  /** Automatic AI quality report for a lecture. */
  AI_LECTURE_REPORT: 'ai_lecture_report',
  /** Generating exam questions out of an uploaded file. */
  AI_EXAM_FROM_FILE: 'ai_exam_from_file',
  /** Generating cover images with AI (upload wizard and the assistant tool). */
  AI_IMAGE_GENERATION: 'ai_image_generation',
  /** Importing students from an external CRM. */
  CRM_STUDENT_IMPORT: 'crm_student_import',
  /** Scoping a member's access down to specific courses instead of all of them. */
  MEMBER_COURSE_ACCESS: 'member_course_access',
  /** Progress analytics broken down by member (curator). */
  MEMBER_ANALYTICS: 'member_analytics',
  /** Minute-by-minute attendance of live sessions (needs Zoom Pro on the provider side too). */
  LIVE_SESSION_ATTENDANCE: 'live_session_attendance',
  /** More than one connected account per video provider — parallel live sessions. */
  LIVE_MULTIPLE_ACCOUNTS: 'live_multiple_accounts',
  /** Connecting external AI apps (Claude, ChatGPT…) to the platform over MCP. */
  MCP_ACCESS: 'mcp_access',
} as const;

export type TariffFeature = (typeof TARIFF_FEATURES)[keyof typeof TARIFF_FEATURES];

/**
 * Countable resources that can be numerically limited by a tariff.
 */
export const TARIFF_LIMIT_RESOURCES = {
  MEMBERS: 'members',
  COURSES: 'courses',
  STUDENTS: 'students',
} as const;

export type TariffLimitResource =
  (typeof TARIFF_LIMIT_RESOURCES)[keyof typeof TARIFF_LIMIT_RESOURCES];

export type TariffLimitUsage = {
  current: number;
  limit: number | null /** null = unlimited */;
};

export type TariffLimits = Partial<Record<TariffLimitResource, TariffLimitUsage>>;

export type Tariff = {
  code: string;
  name: string;
  disabled_features: TariffFeature[];
  limits: TariffLimits;
};

/** Why the upgrade dialog was opened — drives the dialog copy */
export type TariffUpgradeReason =
  | { type: 'feature'; feature: TariffFeature }
  | { type: 'limit'; resource?: TariffLimitResource; usage?: TariffLimitUsage };
